import React from "react";
import { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple } from "@phosphor-icons/react";

const EMPTY = {
  name: "", legal_form: "", siren: "", country: "France",
  sector: "", contact_name: "", contact_email: "", contact_phone: "",
  address: "", notes: "", referentiel: "PCG",
};

export function ClientFormDialog({ client = null }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY);

  const onOpenChange = (v) => {
    if (v && client) {
      setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, client[k] ?? EMPTY[k]])));
    }
    setOpen(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (client) {
        await api.put(`/clients/${client.id}`, form);
        toast.success("Client mis à jour");
      } else {
        await api.post("/clients", form);
        toast.success("Client créé");
      }
      setOpen(false);
      setForm(EMPTY);
      mutate("/clients");
    } catch { toast.error(client ? "Mise à jour impossible" : "Création impossible"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {client ? (
          <Button size="sm" variant="ghost" title="Modifier la fiche" data-testid={`edit-client-btn-${client.id}`}>
            <PencilSimple size={15} className="text-[#2E3192]" />
          </Button>
        ) : (
          <Button data-testid="new-client-btn" className="bg-[#2E3192] hover:bg-[#1E2066]"><Plus size={16} className="mr-1" /> Nouveau client</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl" data-testid="client-form-title">
            {client ? `Modifier — ${client.name}` : "Nouveau client"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label htmlFor="client-name">Raison sociale *</Label><Input id="client-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
          <div><Label htmlFor="client-legal-form">Forme juridique</Label><Input id="client-legal-form" value={form.legal_form} onChange={(e) => setForm({ ...form, legal_form: e.target.value })} placeholder="SARL, SAS, SA…" data-testid="client-legal-form" /></div>
          <div><Label htmlFor="client-siren">SIREN / RCCM</Label><Input id="client-siren" value={form.siren} onChange={(e) => setForm({ ...form, siren: e.target.value })} data-testid="client-siren" /></div>
          <div>
            <Label htmlFor="client-country">Pays</Label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v, referentiel: v === "Cameroun" ? "OHADA" : "PCG" })}>
              <SelectTrigger id="client-country" data-testid="client-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="France">France</SelectItem>
                <SelectItem value="Cameroun">Cameroun</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="client-referentiel">Référentiel</Label>
            <Select value={form.referentiel} onValueChange={(v) => setForm({ ...form, referentiel: v })}>
              <SelectTrigger id="client-referentiel" data-testid="client-referentiel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PCG">Plan Comptable Général (France)</SelectItem>
                <SelectItem value="OHADA">Système comptable OHADA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="client-sector">Secteur d'activité</Label><Input id="client-sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="client-sector" /></div>
          <div><Label htmlFor="client-contact-name">Contact — nom</Label><Input id="client-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="client-contact-name" /></div>
          <div><Label htmlFor="client-contact-email">Contact — email</Label><Input id="client-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-contact-email" /></div>
          <div><Label htmlFor="client-contact-phone">Contact — téléphone</Label><Input id="client-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="client-contact-phone" /></div>
          <div className="md:col-span-2"><Label htmlFor="client-address">Adresse</Label><Input id="client-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address" /></div>
          <div className="md:col-span-2"><Label htmlFor="client-notes">Notes</Label><Textarea id="client-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes" /></div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="submit-client">{client ? "Enregistrer" : "Créer"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
